import React from "react";
import { useMatkaBetPlacement } from "./useMatkaBetPlacement";
import { useDispatch, useSelector } from "react-redux";
import { openLoginModal, openSignupModal } from "../../../features/slices/ModalSlice";
import { QUICK_MARKETS, SINGLE_DIGITS } from "./constants";
import MatkaSessionSelect from "./MatkaSessionSelect";

export default function MatkaSingleDigitBetting({
  selectedMarket,
  selectedGameType,
  sessionStatus,
  onSessionStatusChange,
  digitPoints,
  onPointChange,
  totalBids,
  totalPoints,
  onBackToGameTypes,
  onBackToMarkets,
  onQuickMarketSelect,
  onPlaceBet,
  onClearPoints,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const { openReview, betModals } = useMatkaBetPlacement({
    market: selectedMarket,
    gameType: selectedGameType,
    session: sessionStatus,
    digitPoints,
    onPlaced: onClearPoints,
  });

  const handleContinue = () => {
    if (!totalBids) return;
    if (!user?.userLoggedIn) {
      dispatch(openLoginModal("login"));
      return;
    }
    openReview();
  };

  return (
    <div className="matka-betting-screen-wrapper">
      {/* Top Bar for Betting Screen */}
      <header className="matka-betting-header">
        <div className="container-fluid custom-padding d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          {/* Brand Logo & ONLINE badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              className="matka-betting-brand-text"
              onClick={onBackToMarkets}
            >
              Matka
            </span>
            <span className="matka-online-badge">ONLINE</span>
          </div>

          {/* Quick Market Switcher Pill */}
          <div className="matka-quick-market-switcher d-flex align-items-center">
            {QUICK_MARKETS.map((m, idx) => {
              const isCurrent = (selectedMarket?.id || "mahadevi") === m.id;
              return (
                <React.Fragment key={m.id}>
                  <button
                    type="button"
                    className={`matka-quick-market-btn ${isCurrent ? "active" : ""}`}
                    onClick={() => onQuickMarketSelect(m.id)}
                  >
                    {m.icon && <span className="me-1">{m.icon}</span>}
                    {m.name}
                  </button>
                  {idx < QUICK_MARKETS.length - 1 && (
                    <span className="matka-switcher-dot">•</span>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Right Auth Buttons if not logged in */}
          <div className="d-flex align-items-center gap-2">
            {!user.userLoggedIn ? (
              <>
                <button
                  type="button"
                  className="matka-betting-login-btn"
                  onClick={() => dispatch(openLoginModal("login"))}
                >
                  LOGIN
                </button>
                <button
                  type="button"
                  className="matka-betting-signup-btn"
                  onClick={() => dispatch(openSignupModal())}
                >
                  SIGN UP
                </button>
              </>
            ) : (
              <span className="text-warning fw-bold small">
                Welcome, {user.userData?.username || "Player"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Betting Screen Body */}
      <main className="container-fluid custom-padding matka-betting-body pt-3 pb-5">
        {/* Back to Market Link */}
        <button
          type="button"
          className="matka-back-to-market-link d-inline-flex align-items-center mb-2"
          onClick={onBackToGameTypes}
        >
          <i className="bi bi-chevron-left me-1"></i>
          Back to{" "}
          {selectedMarket.name.charAt(0) +
            selectedMarket.name.slice(1).toLowerCase()}
        </button>

        {/* Title & Subtitle */}
        <h1 className="matka-betting-title">{selectedGameType.title}</h1>
        <p className="matka-betting-subtitle mb-3">
          {selectedGameType.subtitle}
        </p>

        {/* Game Type (Open/Close) Selector */}
        <div className="matka-game-type-selector-wrapper mb-3">
          <label className="matka-game-type-label">GAME TYPE</label>
          <MatkaSessionSelect
            betDirection={selectedGameType?.betDirection}
            value={sessionStatus}
            onChange={onSessionStatusChange}
          />
        </div>

        {/* Choose Digit & Enter Points Section */}
        <div className="matka-digits-section mb-3">
          <h3 className="matka-digits-section-heading mb-2">
            CHOOSE DIGIT &amp; ENTER POINTS
          </h3>

          <div className="matka-digits-grid">
            {SINGLE_DIGITS.map((digit) => (
              <div
                key={digit}
                className={`matka-digit-item-card ${
                  digitPoints[digit] ? "active-input" : ""
                }`}
              >
                <div className="matka-digit-badge">{digit}</div>
                <input
                  type="text"
                  inputMode="numeric"
                  className="matka-digit-points-input"
                  placeholder="Enter Points"
                  value={digitPoints[digit]}
                  onChange={(e) => onPointChange(digit, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Fixed Bottom Action Bar */}
      <div
        className="matka-betting-bottom-bar"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          zIndex: 9999,
        }}
      >
        <div className="container-fluid custom-padding d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
          {/* Summary counters */}
          <div className="d-flex align-items-center gap-3">
            <div className="matka-summary-pill d-flex align-items-center gap-2">
              <span className="matka-summary-num">{totalBids}</span>
              <span className="matka-summary-label">TOTAL BIDS</span>
            </div>

            <div className="matka-summary-pill d-flex align-items-center gap-2">
              <span className="matka-summary-num">{totalPoints}</span>
              <span className="matka-summary-label">TOTAL POINTS</span>
            </div>
          </div>

          {/* Continue Place Bet Button */}
          <button
            type="button"
            className="matka-continue-bet-btn"
            onClick={handleContinue}
            disabled={totalBids === 0}
          >
            CONTINUE PLACE BET
          </button>
        </div>
      </div>

      {betModals}
    </div>
  );
}
